import { buildInfoHeaders } from '@agentic-flow-studio/shared/lib/build-info';
import type { EmailThreadMetadata } from '@agentic-flow-studio/shared/types/email';
import { getBetterAuthToken } from './betterAuth';

const BASE_URL = import.meta.env.VITE_WORKER_URL;

async function getHeaders() {
  const workspaceId = localStorage.getItem('workspaceId');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...buildInfoHeaders(),
  };

  const token = await getBetterAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (workspaceId) {
    headers['x-workspace-id'] = workspaceId;
  }

  return headers;
}

export const harborApi = {
  async ingestThread(threadId: string) {
    const response = await fetch(`${BASE_URL}/api/threads/ingest`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ threadId }),
    });
    if (!response.ok) throw new Error('Ingest failed');
    return response.json();
  },

  async getSummary(accountEmails?: string[]) {
    const url = new URL(`${BASE_URL}/api/dashboard/summary`);
    if (accountEmails?.length) url.searchParams.append('accountEmails', accountEmails.join(','));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch summary failed');
    return response.json();
  },

  async getThreads(accountEmails?: string[]) {
    const url = new URL(`${BASE_URL}/api/threads`);
    if (accountEmails?.length) url.searchParams.append('accountEmails', accountEmails.join(','));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch threads failed');
    return response.json() as Promise<EmailThreadMetadata[]>;
  },

  async getLabels() {
    const response = await fetch(`${BASE_URL}/api/gmail/labels`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch labels failed');
    return response.json();
  },

  async getLabelWeights() {
    const response = await fetch(`${BASE_URL}/api/labels/weights`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch label weights failed');
    return response.json();
  },

  async updateLabelWeight(labelId: string, weight: number) {
    const response = await fetch(`${BASE_URL}/api/labels/weights`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ labelId, weight }),
    });
    if (!response.ok) throw new Error('Update label weight failed');
    return response.json();
  },

  async getDomainWeights() {
    const response = await fetch(`${BASE_URL}/api/domains/weights`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch domain weights failed');
    return response.json();
  },

  async updateDomainWeight(domain: string, weight: number) {
    const response = await fetch(`${BASE_URL}/api/domains/weights`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ domain, weight }),
    });
    if (!response.ok) throw new Error('Update domain weight failed');
    return response.json();
  },

  async getCategories() {
    const response = await fetch(`${BASE_URL}/api/categories`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch categories failed');
    return response.json();
  },

  async updateCategory(category: { id?: number; name: string; color: string; weight: number }) {
    const response = await fetch(`${BASE_URL}/api/categories`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(category),
    });
    if (!response.ok) throw new Error('Update category failed');
    return response.json();
  },

  async deleteCategory(id: number) {
    const response = await fetch(`${BASE_URL}/api/categories/${id}`, {
      method: 'DELETE',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Delete category failed');
    return response.json();
  },

  async getLabelMappings() {
    const response = await fetch(`${BASE_URL}/api/label-mappings`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch mappings failed');
    return response.json();
  },

  async updateLabelMappings(labelId: string, categoryIds: number[]) {
    const response = await fetch(`${BASE_URL}/api/label-mappings`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ labelId, categoryIds }),
    });
    if (!response.ok) throw new Error('Update mappings failed');
    return response.json();
  },

  async suggestMappings(labels: string[], categories: { id: number; name: string }[]) {
    const response = await fetch(`${BASE_URL}/api/suggest-mappings`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ labels, categories }),
    });
    if (!response.ok) throw new Error('Suggest mappings failed');
    return response.json();
  },

  async suggestCategories(labels: string[]) {
    const response = await fetch(`${BASE_URL}/api/suggest-categories`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ labels }),
    });
    if (!response.ok) throw new Error('Suggest categories failed');
    return response.json();
  },

  async getAccounts() {
    const response = await fetch(`${BASE_URL}/api/accounts`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch accounts failed');
    return response.json();
  },

  async getAccountPermissions(accountId: number) {
    const response = await fetch(`${BASE_URL}/api/accounts/${accountId}/permissions`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch permissions failed');
    return response.json();
  },

  async updateAccountPermissions(accountId: number, userId: number, action: 'add' | 'remove') {
    const response = await fetch(`${BASE_URL}/api/accounts/${accountId}/permissions`, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify({ userId, action }),
    });
    if (!response.ok) throw new Error('Update account permissions failed');
    return response.json();
  },

  async getWorkspaceMembers() {
    const response = await fetch(`${BASE_URL}/api/workspace/members`, {
      method: 'GET',
      headers: await getHeaders(),
    });
    if (!response.ok) throw new Error('Fetch members failed');
    return response.json() as Promise<
      Array<{ id: number; email: string; fullName: string | null }>
    >;
  },
};
